// ─── Theme definitions ────────────────────────────────────────────────────────
// CSS variables applied to :root via applyTheme().
// Major surfaces: bg-root, bg-header, bg-card, bg-subtle, bg-hover, bg-input
// Text: text-primary, text-secondary, text-muted
// Borders: border
// Buttons: btn-primary, btn-primary-txt, btn-ghost-bg, btn-ghost-txt, btn-ghost-border
// Timeline cards: card-bg, card-ai-bg, card-nc-bg, card-border-def
// Timeline headers: sprint-next-bg
// Completed section: completed-bg, completed-border

export const THEMES = {
  modern: {
    id: "modern", name: "Violet",
    // Visibly purple-tinted surfaces — not "basically white with a button"
    preview: ["#EDEAF6", "#5540F0", "#16121E"],
    vars: {
      "--bg-root":            "#EDEAF6",
      "--bg-header":          "#F4F2FA",
      "--bg-card":            "#FAFAFE",
      "--bg-subtle":          "#E3DFF2",
      "--bg-hover":           "#D6D0EA",
      "--bg-input":           "#F4F2FA",
      "--border":             "#C8C2E4",
      "--text-primary":       "#16121E",
      "--text-secondary":     "#524C6C",
      "--text-muted":         "#9890B4",
      "--btn-primary":        "linear-gradient(135deg,#5540F0,#4028E4)",
      "--btn-primary-txt":    "#FFFFFF",
      "--btn-ghost-bg":       "#FAFAFE",
      "--btn-ghost-txt":      "#524C6C",
      "--btn-ghost-border":   "#C8C2E4",
      "--overlay":            "rgba(14,10,24,0.55)",
      "--card-bg":            "#FAFAFE",
      "--card-ai-bg":         "#E8E2FF",
      "--card-nc-bg":         "#FEFCE6",
      "--card-border-def":    "#C8C2E4",
      "--sprint-next-bg":     "#DDD6F8",
      "--completed-bg":       "#EAE4FF",
      "--completed-border":   "#B8AEFA",
      "--accent":             "#5540F0",
      "--accent-dark":        "#3A20D8",
      "--accent-glow":        "rgba(85,64,240,0.18)",
      "--accent-glow-strong": "rgba(85,64,240,0.48)",
      "--scrollbar-thumb":    "rgba(0,0,0,0.14)",
      "--scrollbar-thumb-hover": "rgba(0,0,0,0.26)",
      "--color-danger":       "#EF4444",
      "--color-danger-bg":    "#FEF2F2",
      "--color-danger-border":"#FCA5A5",
      "--color-success":      "#22C55E",
      "--color-success-bg":   "#F0FDF4",
      "--color-success-border":"#86EFAC",
      "--color-warning":      "#F59E0B",
      "--color-warning-bg":   "#FFFBEB",
      "--color-warning-border":"#FDE68A",
      "--color-info":         "#3B82F6",
      "--color-info-bg":      "#EFF6FF",
      "--color-info-border":  "#BFDBFE",
    },
  },
  light: {
    id: "light", name: "Light",
    // Warm amber CTA on cream — nothing like the generic blue-on-white it was
    preview: ["#FBF7F0", "#D97706", "#1E1812"],
    vars: {
      "--bg-root":            "#FBF7F0",
      "--bg-header":          "#FEFCF7",
      "--bg-card":            "#FEFCF7",
      "--bg-subtle":          "#F2EBD8",
      "--bg-hover":           "#EAE0C8",
      "--bg-input":           "#FBF7F0",
      "--border":             "#DDD0B4",
      "--text-primary":       "#1E1812",
      "--text-secondary":     "#6B5E4C",
      "--text-muted":         "#B0A088",
      "--btn-primary":        "linear-gradient(135deg,#D97706,#B45309)",
      "--btn-primary-txt":    "#FFFFFF",
      "--btn-ghost-bg":       "#FEFCF7",
      "--btn-ghost-txt":      "#6B5E4C",
      "--btn-ghost-border":   "#DDD0B4",
      "--overlay":            "rgba(20,14,8,0.44)",
      "--card-bg":            "#FEFCF7",
      "--card-ai-bg":         "#F2EBD8",
      "--card-nc-bg":         "#FEF8EA",
      "--card-border-def":    "#DDD0B4",
      "--sprint-next-bg":     "#EEE0C0",
      "--completed-bg":       "#F2EBD8",
      "--completed-border":   "#D4B87A",
      "--accent":             "#D97706",
      "--accent-dark":        "#B45309",
      "--accent-glow":        "rgba(217,119,6,0.18)",
      "--accent-glow-strong": "rgba(217,119,6,0.48)",
      "--scrollbar-thumb":    "rgba(0,0,0,0.14)",
      "--scrollbar-thumb-hover": "rgba(0,0,0,0.26)",
      "--color-danger":       "#EF4444",
      "--color-danger-bg":    "#FEF2F2",
      "--color-danger-border":"#FCA5A5",
      "--color-success":      "#22C55E",
      "--color-success-bg":   "#F0FDF4",
      "--color-success-border":"#86EFAC",
      "--color-warning":      "#F59E0B",
      "--color-warning-bg":   "#FFFBEB",
      "--color-warning-border":"#FDE68A",
      "--color-info":         "#3B82F6",
      "--color-info-bg":      "#EFF6FF",
      "--color-info-border":  "#BFDBFE",
    },
  },
  dark: {
    id: "dark", name: "Dark",
    // Electric emerald on near-true-black — completely different from the old GitHub blue clone
    preview: ["#080A09", "#10B981", "#E8F5EF"],
    vars: {
      "--bg-root":            "#080A09",
      "--bg-header":          "#0C0F0D",
      "--bg-card":            "#131916",
      "--bg-subtle":          "#080A09",
      "--bg-hover":           "#1B2420",
      "--bg-input":           "#0C0F0D",
      "--border":             "#223028",
      "--text-primary":       "#E8F5EF",
      "--text-secondary":     "#78A088",
      "--text-muted":         "#486858",
      "--btn-primary":        "linear-gradient(135deg,#10B981,#059669)",
      "--btn-primary-txt":    "#FFFFFF",
      "--btn-ghost-bg":       "#1B2420",
      "--btn-ghost-txt":      "#78A088",
      "--btn-ghost-border":   "#223028",
      "--overlay":            "rgba(0,0,0,0.82)",
      "--card-bg":            "#131916",
      "--card-ai-bg":         "#061A10",
      "--card-nc-bg":         "#1A1400",
      "--card-border-def":    "#223028",
      "--sprint-next-bg":     "#071C12",
      "--completed-bg":       "#061810",
      "--completed-border":   "#0C3820",
      "--accent":             "#10B981",
      "--accent-dark":        "#059669",
      "--accent-glow":        "rgba(16,185,129,0.18)",
      "--accent-glow-strong": "rgba(16,185,129,0.48)",
      "--scrollbar-thumb":    "rgba(255,255,255,0.18)",
      "--scrollbar-thumb-hover": "rgba(255,255,255,0.32)",
      "--color-danger":       "#F87171",
      "--color-danger-bg":    "#2D1010",
      "--color-danger-border":"#7F1D1D",
      "--color-success":      "#4ADE80",
      "--color-success-bg":   "#052E16",
      "--color-success-border":"#166534",
      "--color-warning":      "#FCD34D",
      "--color-warning-bg":   "#1C1200",
      "--color-warning-border":"#92400E",
      "--color-info":         "#60A5FA",
      "--color-info-bg":      "#0C1A2E",
      "--color-info-border":  "#1E3A5F",
    },
  },
  gasbuddy: {
    id: "gasbuddy", name: "GasBuddy",
    // Warm matte-black + rich amber-gold (not neon orange)
    preview: ["#0C0A07", "#F59E0B", "#F4EFE4"],
    vars: {
      "--bg-root":            "#0C0A07",
      "--bg-header":          "#161410",
      "--bg-card":            "#1E1C17",
      "--bg-subtle":          "#0C0A07",
      "--bg-hover":           "#28241A",
      "--bg-input":           "#161410",
      "--border":             "#302C1E",
      "--text-primary":       "#F4EFE4",
      "--text-secondary":     "#A89C84",
      "--text-muted":         "#706448",
      "--btn-primary":        "linear-gradient(135deg,#F59E0B,#D97706)",
      "--btn-primary-txt":    "#1A1000",
      "--btn-ghost-bg":       "#1E1C17",
      "--btn-ghost-txt":      "#A89C84",
      "--btn-ghost-border":   "#302C1E",
      "--overlay":            "rgba(0,0,0,0.84)",
      "--card-bg":            "#221F18",
      "--card-ai-bg":         "#181620",
      "--card-nc-bg":         "#1E1400",
      "--card-border-def":    "#38341E",
      "--sprint-next-bg":     "#1A1000",
      "--completed-bg":       "#161410",
      "--completed-border":   "#302C1E",
      "--accent":             "#F59E0B",
      "--accent-dark":        "#D97706",
      "--accent-glow":        "rgba(245,158,11,0.18)",
      "--accent-glow-strong": "rgba(245,158,11,0.48)",
      "--scrollbar-thumb":    "rgba(255,255,255,0.18)",
      "--scrollbar-thumb-hover": "rgba(255,255,255,0.32)",
      "--color-danger":       "#F87171",
      "--color-danger-bg":    "#2D1010",
      "--color-danger-border":"#7F1D1D",
      "--color-success":      "#4ADE80",
      "--color-success-bg":   "#052E16",
      "--color-success-border":"#166534",
      "--color-warning":      "#FCD34D",
      "--color-warning-bg":   "#1C1200",
      "--color-warning-border":"#92400E",
      "--color-info":         "#60A5FA",
      "--color-info-bg":      "#0C1A2E",
      "--color-info-border":  "#1E3A5F",
    },
  },
  midnight: {
    id: "midnight", name: "Midnight",
    preview: ["#0D1117", "#58A6FF", "#E6EDF3"],
    vars: {
      "--bg-root":            "#0D1117",
      "--bg-header":          "#161B22",
      "--bg-card":            "#1C2128",
      "--bg-subtle":          "#0D1117",
      "--bg-hover":           "#21262D",
      "--bg-input":           "#161B22",
      "--border":             "#30363D",
      "--text-primary":       "#E6EDF3",
      "--text-secondary":     "#8B949E",
      "--text-muted":         "#484F58",
      "--btn-primary":        "linear-gradient(135deg,#1F6FEB,#1A5FD4)",
      "--btn-primary-txt":    "#FFFFFF",
      "--btn-ghost-bg":       "#21262D",
      "--btn-ghost-txt":      "#8B949E",
      "--btn-ghost-border":   "#30363D",
      "--overlay":            "rgba(1,4,9,0.86)",
      "--card-bg":            "#1C2128",
      "--card-ai-bg":         "#0D1626",
      "--card-nc-bg":         "#1C1800",
      "--card-border-def":    "#30363D",
      "--sprint-next-bg":     "#0A1628",
      "--completed-bg":       "#0A1A10",
      "--completed-border":   "#0E3820",
      "--accent":             "#58A6FF",
      "--accent-dark":        "#1F6FEB",
      "--accent-glow":        "rgba(88,166,255,0.18)",
      "--accent-glow-strong": "rgba(88,166,255,0.44)",
      "--scrollbar-thumb":    "rgba(255,255,255,0.14)",
      "--scrollbar-thumb-hover": "rgba(255,255,255,0.28)",
      "--color-danger":       "#F87171",
      "--color-danger-bg":    "#2D1010",
      "--color-danger-border":"#7F1D1D",
      "--color-success":      "#4ADE80",
      "--color-success-bg":   "#052E16",
      "--color-success-border":"#166534",
      "--color-warning":      "#FCD34D",
      "--color-warning-bg":   "#1C1200",
      "--color-warning-border":"#92400E",
      "--color-info":         "#60A5FA",
      "--color-info-bg":      "#0C1A2E",
      "--color-info-border":  "#1E3A5F",
    },
  },
  ocean: {
    id: "ocean", name: "Ocean",
    preview: ["#F0F9FF", "#0891B2", "#0C2340"],
    vars: {
      "--bg-root":            "#F0F9FF",
      "--bg-header":          "#FFFFFF",
      "--bg-card":            "#FFFFFF",
      "--bg-subtle":          "#E0F2FE",
      "--bg-hover":           "#BAE6FD",
      "--bg-input":           "#F0F9FF",
      "--border":             "#BAE6FD",
      "--text-primary":       "#0C2340",
      "--text-secondary":     "#1E4E7E",
      "--text-muted":         "#64A0C8",
      "--btn-primary":        "linear-gradient(135deg,#0891B2,#0E7490)",
      "--btn-primary-txt":    "#FFFFFF",
      "--btn-ghost-bg":       "#FFFFFF",
      "--btn-ghost-txt":      "#1E4E7E",
      "--btn-ghost-border":   "#BAE6FD",
      "--overlay":            "rgba(12,35,64,0.56)",
      "--card-bg":            "#FFFFFF",
      "--card-ai-bg":         "#EFF6FF",
      "--card-nc-bg":         "#FEFCE8",
      "--card-border-def":    "#BAE6FD",
      "--sprint-next-bg":     "#CFFAFE",
      "--completed-bg":       "#ECFDF5",
      "--completed-border":   "#6EE7B7",
      "--accent":             "#0891B2",
      "--accent-dark":        "#0E7490",
      "--accent-glow":        "rgba(8,145,178,0.18)",
      "--accent-glow-strong": "rgba(8,145,178,0.44)",
      "--scrollbar-thumb":    "rgba(0,0,0,0.14)",
      "--scrollbar-thumb-hover": "rgba(0,0,0,0.26)",
      "--color-danger":       "#EF4444",
      "--color-danger-bg":    "#FEF2F2",
      "--color-danger-border":"#FCA5A5",
      "--color-success":      "#22C55E",
      "--color-success-bg":   "#F0FDF4",
      "--color-success-border":"#86EFAC",
      "--color-warning":      "#F59E0B",
      "--color-warning-bg":   "#FFFBEB",
      "--color-warning-border":"#FDE68A",
      "--color-info":         "#3B82F6",
      "--color-info-bg":      "#EFF6FF",
      "--color-info-border":  "#BFDBFE",
    },
  },
};

export const THEME_LIST = Object.values(THEMES);

export function applyTheme(themeId) {
  const theme = THEMES[themeId] || THEMES.modern;
  const root = document.documentElement;
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
}
